import React, { useState, useEffect } from 'react';

const SMSMarketingPopup = ({ onClose, initialPhone = '' }) => {
  const [phone, setPhone] = useState(initialPhone);
  const [consented, setConsented] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Trap focus and handle ESC
  useEffect(() => {
    const handleKey = (e) => {
      if (e?.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!phone?.trim()) {
      setError('Vennligst skriv inn ditt mobilnummer');
      return;
    }
    if (!consented) {
      setError('Du må godta vilkårene for å fortsette');
      return;
    }
    setError('');
    setIsSubmitting(true);
    await new Promise(r => setTimeout(r, 1000));
    setIsSubmitting(false);
    setSubmitted(true);
  };

  const benefits = [
    { icon: '⚡', title: 'Eksklusive tilbud', desc: 'Kun for SMS-abonnenter' },
    { icon: '🚦', title: 'Bomtakst-varsler', desc: 'Sanntidsoppdateringer' },
    { icon: '🎯', title: 'Personlige tips', desc: 'Basert på dine ruter' },
  ];

  return (
    <div
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(10,12,14,0.85)',
        backdropFilter: 'blur(6px)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
      }}
      onClick={(e) => { if (e?.target === e?.currentTarget) onClose?.(); }}
    >
      {/* Modal container */}
      <div style={{
        position: 'relative',
        width: '100%',
        maxWidth: '480px',
        background: 'var(--color-background)',
        border: '1px solid rgba(224,228,232,0.1)',
        borderTop: '4px solid #FF5722',
        borderRadius: '2px',
        overflow: 'hidden',
        animation: 'slideUp 0.3s ease-out',
      }}>
        {/* Background geometry */}
        <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '160px', height: '160px', border: '2px solid rgba(255,87,34,0.08)', borderRadius: '2px', transform: 'rotate(20deg)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-30px', left: '-30px', width: '120px', height: '120px', border: '1px solid rgba(0,79,152,0.08)', borderRadius: '2px', transform: 'rotate(-15deg)', pointerEvents: 'none' }} />

        {/* Close button */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '16px', right: '16px',
            width: '32px', height: '32px',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(224,228,232,0.12)',
            borderRadius: '2px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 10,
            transition: 'background 200ms ease',
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
          aria-label="Lukk"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--color-muted-foreground)" strokeWidth="2.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {!submitted ? (
          <div style={{ padding: '36px 32px 32px' }}>
            {/* Header */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
                <div style={{ width: '36px', height: '36px', background: '#FF5722', borderRadius: '2px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                    <line x1="12" y1="18" x2="12.01" y2="18" />
                  </svg>
                </div>
                <div style={{ width: '1px', height: '36px', background: 'rgba(255,87,34,0.3)' }} />
                <div>
                  <p style={{ fontFamily: 'var(--font-heading)', fontSize: '10px', fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#FF5722', marginBottom: '2px' }}>SMS-markedsføring</p>
                  <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>Veiassistent Norge</p>
                </div>
              </div>
              <h2 style={{
                fontFamily: 'var(--font-heading)',
                fontSize: '28px',
                fontWeight: 700,
                color: 'var(--color-foreground)',
                letterSpacing: '0.01em',
                lineHeight: 1.2,
                marginBottom: '10px',
              }}>
                Hold deg oppdatert
                <span style={{ display: 'block', color: '#FF5722' }}>via SMS</span>
              </h2>
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.6 }}>
                Registrer deg for å motta eksklusive tilbud og viktige oppdateringer direkte på mobilen.
              </p>
            </div>

            {/* Benefits */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '28px' }}>
              {benefits?.map((b, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid rgba(224,228,232,0.07)',
                  borderLeft: '3px solid rgba(255,87,34,0.4)',
                  borderRadius: '0 2px 2px 0',
                }}>
                  <span style={{ fontSize: '18px', flexShrink: 0 }}>{b?.icon}</span>
                  <div>
                    <p style={{ fontFamily: 'var(--font-heading)', fontSize: '13px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '1px' }}>{b?.title}</p>
                    <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>{b?.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Frequency info */}
            <div style={{
              padding: '12px 16px',
              background: 'rgba(0,79,152,0.06)',
              border: '1px solid rgba(0,79,152,0.1)',
              borderRadius: '2px',
              marginBottom: '24px',
              display: 'flex', gap: '16px',
            }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <p style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '2px' }}>Maks 2/uke</p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>Frekvens</p>
              </div>
              <div style={{ width: '1px', background: 'rgba(224,228,232,0.1)' }} />
              <div style={{ textAlign: 'center', flex: 1 }}>
                <p style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '2px' }}>Gratis</p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>Ingen kostnad</p>
              </div>
              <div style={{ width: '1px', background: 'rgba(224,228,232,0.1)' }} />
              <div style={{ textAlign: 'center', flex: 1 }}>
                <p style={{ fontFamily: 'var(--font-heading)', fontSize: '14px', fontWeight: 600, color: 'var(--color-foreground)', marginBottom: '2px' }}>Enkel</p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '12px', color: 'var(--color-muted-foreground)' }}>Avmelding</p>
              </div>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '16px' }}>
                <label style={{ fontFamily: 'var(--font-heading)', fontSize: '11px', fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-muted-foreground)', display: 'block', marginBottom: '8px' }}>
                  Mobilnummer
                </label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontFamily: 'var(--font-body)', fontSize: '14px', color: 'var(--color-muted-foreground)' }}>🇳🇴 +47</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={e => { setPhone(e?.target?.value); setError(''); }}
                    placeholder="000 00 000"
                    style={{
                      width: '100%',
                      background: 'rgba(255,255,255,0.03)',
                      border: '1px solid rgba(224,228,232,0.12)',
                      borderBottom: '2px solid rgba(255,87,34,0.4)',
                      borderRadius: '0',
                      padding: '13px 14px 13px 80px',
                      fontFamily: 'var(--font-body)',
                      fontSize: '14px',
                      fontWeight: 400,
                      color: 'var(--color-foreground)',
                      outline: 'none',
                      boxSizing: 'border-box',
                      transition: 'border-color 200ms ease',
                    }}
                    onFocus={e => e.target.style.borderBottomColor = '#FF5722'}
                    onBlur={e => e.target.style.borderBottomColor = 'rgba(255,87,34,0.4)'}
                  />
                </div>
              </div>

              {/* Consent checkbox */}
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', cursor: 'pointer', marginBottom: '20px' }}>
                <div style={{ position: 'relative', flexShrink: 0, marginTop: '2px' }}>
                  <input
                    type="checkbox"
                    checked={consented}
                    onChange={e => { setConsented(e?.target?.checked); setError(''); }}
                    style={{ position: 'absolute', opacity: 0, width: '20px', height: '20px', cursor: 'pointer', zIndex: 1 }}
                  />
                  <div style={{
                    width: '20px', height: '20px',
                    background: consented ? '#FF5722' : 'transparent',
                    border: `2px solid ${consented ? '#FF5722' : 'rgba(224,228,232,0.3)'}`,
                    borderRadius: '2px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all 200ms ease',
                  }}>
                    {consented && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </div>
                </div>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', fontWeight: 400, color: 'var(--color-muted-foreground)', lineHeight: 1.5 }}>
                  Jeg godtar å motta markedsføring via SMS fra Veiassistent. Jeg kan når som helst avslutte ved å svare STOPP.
                </p>
              </label>

              {error && (
                <p style={{ fontFamily: 'var(--font-caption)', fontSize: '12px', color: '#EF4444', marginBottom: '16px' }}>{error}</p>
              )}

              {/* CTA buttons */}
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  width: '100%',
                  background: isSubmitting ? 'rgba(255,87,34,0.6)' : '#FF5722',
                  border: '2px solid #FF5722',
                  borderRadius: '0',
                  padding: '14px 24px',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '16px',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: '#FFFFFF',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  marginBottom: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 250ms ease',
                  clipPath: 'polygon(0 0, calc(100% - 10px) 0, 100% 10px, 100% 100%, 10px 100%, 0 calc(100% - 10px))',
                }}
                onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.background = '#E64A19'; }}
                onMouseLeave={e => { if (!isSubmitting) e.currentTarget.style.background = '#FF5722'; }}
              >
                {isSubmitting ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                      <path d="M21 12a9 9 0 11-6.219-8.56" />
                    </svg>
                    Registrerer...
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
                      <line x1="12" y1="18" x2="12.01" y2="18" />
                    </svg>
                    Aktiver SMS-varsler
                  </>
                )}
              </button>

              <button
                type="button"
                onClick={onClose}
                style={{
                  width: '100%',
                  background: 'transparent',
                  border: '1px solid rgba(0,79,152,0.25)',
                  borderRadius: '2px',
                  padding: '12px 24px',
                  fontFamily: 'var(--font-heading)',
                  fontSize: '13px',
                  fontWeight: 500,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: 'var(--color-primary)',
                  cursor: 'pointer',
                  transition: 'all 200ms ease',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,79,152,0.06)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                Ikke nå
              </button>
            </form>

            {/* Privacy note */}
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '11px', fontWeight: 300, color: 'rgba(224,228,232,0.35)', lineHeight: 1.7, marginTop: '16px', textAlign: 'center' }}>
              Vi deler aldri ditt nummer med tredjeparter. Dine data behandles i henhold til norsk personvernlovgivning (GDPR). Svar STOPP for å avslutte når som helst.
            </p>
          </div>
        ) : (
          /* Success state */
          <div style={{ padding: '48px 32px', textAlign: 'center' }}>
            <div style={{
              width: '64px', height: '64px',
              background: 'rgba(255,87,34,0.1)',
              border: '2px solid rgba(255,87,34,0.4)',
              borderRadius: '2px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              margin: '0 auto 24px',
              transform: 'rotate(3deg)',
            }}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#FF5722" strokeWidth="2.5">
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <h3 style={{ fontFamily: 'var(--font-heading)', fontSize: '24px', fontWeight: 700, color: 'var(--color-foreground)', marginBottom: '12px', letterSpacing: '0.02em' }}>
              Du er påmeldt!
            </h3>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '15px', color: 'var(--color-muted-foreground)', lineHeight: 1.6, marginBottom: '8px' }}>
              SMS-varsler er aktivert for
            </p>
            <p style={{ fontFamily: 'var(--font-heading)', fontSize: '18px', fontWeight: 600, color: '#FF5722', marginBottom: '28px' }}>
              +47 {phone}
            </p>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: '13px', color: 'var(--color-muted-foreground)', marginBottom: '28px' }}>
              Du vil motta maks 2 meldinger per uke. Svar STOPP for å avslutte.
            </p>
            <button
              onClick={onClose}
              style={{
                background: '#FF5722',
                border: '2px solid #FF5722',
                borderRadius: '2px',
                padding: '12px 32px',
                fontFamily: 'var(--font-heading)',
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#FFFFFF',
                cursor: 'pointer',
              }}
            >
              Lukk
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SMSMarketingPopup;
